import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { useSearchParams, useParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';

import { FactsTable } from './components/facts-table';
import { FactsTableHeader } from './components/facts-table-header';
import { FactsTableLoader } from './components/facts-table-loader';
import { RunType } from '../../../../../../../../../backend/src/run/run-type';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetCaseIssues } from '@/api/queries/useGetCaseThemes';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetPersonNames } from '@/api/queries/useGetPersonNames';
import { useGetSubset } from '@/api/queries/useGetSubset';
import useGetSubsetFactAllIds from '@/api/queries/useGetSubsetFactAllIds';
import useGetSubsetFacts from '@/api/queries/useGetSubsetFacts';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { useFactNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { FilterPills } from '@/new/components/filter-pills';
import { PageContainer } from '@/new/components/page-container';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { FactFilters, Relevance } from '@/types';
import { buildFactFilterPills } from '@/utils/buildFilterPills';
import { removeFilterValue } from '@/utils/filterUtils';

export function Facts() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [docId, setDocId] = useState<string | null>(null);
  const [factId, setFactId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialise filters with sessionStorage persistence
  const defaultFilters: FactFilters = {
    docIds: [],
    excludedDocIds: [],
    relevance: [],
    classification: [],
    keyFacts: false,
    disputedFacts: false,
    startDate: null,
    endDate: null,
    runIds: [],
    personIds: [],
    documentTypes: [],
    issueIds: [],
    searchQuery: '',
    factIds: [],
  };
  const [filters, setFilters] = useSessionStorage<FactFilters>(`case-${subsetId}-facts-table-filters`, defaultFilters);
  const [searchQueryInput, setSearchQueryInput] = useState(filters.searchQuery ?? '');
  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set());

  const { db, selectMultipleFacts, getAllSelectedFactIds, selectFact, deselectFact, deselectAllFacts } = useIndexedDb();

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useFactNaturalLanguageFilters({
    matterId,
    subsetId,
    onFiltersChange: (newFilters: FactFilters) => {
      setFilters(newFilters);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    onSearchChange: (searchQuery: string) => {
      setFilters((prev) => ({ ...prev, searchQuery: searchQuery }));
    },
  });

  const { data: personNames } = useGetPersonNames(matterId, subsetId, filters.personIds ?? []);
  const personsMapRef = useRef<Map<string, string>>(new Map());
  const personsMap = useMemo(() => {
    if (personNames) {
      for (const { id, name } of personNames) {
        personsMapRef.current.set(id, name);
      }
    }
    return personsMapRef.current;
  }, [personNames]);
  const { data: issuesWithCounts = [] } = useGetCaseIssues({ matterId, subsetId });
  const issuesMap = useMemo(() => new Map(issuesWithCounts.map((t) => [t.id, t.name])), [issuesWithCounts]);
  const {
    data: responseFacts,
    isLoading,
    refetch: refetchCaseFacts,
  } = useGetSubsetFacts({
    matterId,
    subsetId,
    searchQuery: filters.searchQuery ?? undefined,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters,
    paginated: true,
  });
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: runs } = useGetMatterRuns(matterId);

  const { data: allIdsData, isLoading: isAllIdsLoading } = useGetSubsetFactAllIds({
    matterId,
    subsetId,
    searchQuery: filters.searchQuery ?? undefined,
    filters,
    enabled: !isLoading && !!responseFacts,
  });

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Facts', path: 'facts' },
  });

  // Load selected facts from IndexedDB
  useEffect(() => {
    async function loadSelectedFacts() {
      if (!db || !subsetId) return;
      const selectedIds = await getAllSelectedFactIds(subsetId);
      setSelectedFactIds(new Set(selectedIds));
    }
    loadSelectedFacts();
  }, [db, subsetId, getAllSelectedFactIds]);

  // Handlers
  const handleToggleAll = async ({ checked: toggleValue }: { checked: boolean }): Promise<void> => {
    if (!subsetId) return;

    trackCustomEvent(toggleValue ? TrackingEventsV2.FactToggleAllOn : TrackingEventsV2.FactToggleAllOff, {
      matterId,
      isAllDocuments: subset?.isAllDocuments,
    });

    if (toggleValue) {
      if (!allIdsData) return;

      await selectMultipleFacts(subsetId, allIdsData.factIds);
      const selectedIds = await getAllSelectedFactIds(subsetId);
      setSelectedFactIds(new Set(selectedIds));
    } else {
      await deselectAllFacts(subsetId);
      const selectedIds = await getAllSelectedFactIds(subsetId);
      setSelectedFactIds(new Set(selectedIds));
    }
  };

  const handleFactSelectionChange = useCallback(
    async (factId: string, selected: boolean) => {
      // Optimistic update for responsive UI
      setSelectedFactIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          next.add(factId);
        } else {
          next.delete(factId);
        }
        return next;
      });

      // Persist to IndexedDB with rollback on error
      try {
        if (selected) {
          await selectFact(subsetId, factId);
        } else {
          await deselectFact(subsetId, factId);
        }
      } catch (error) {
        // Rollback on error
        setSelectedFactIds((prev) => {
          const next = new Set(prev);
          if (selected) {
            next.delete(factId);
          } else {
            next.add(factId);
          }
          return next;
        });
        console.error('Failed to update fact selection:', error);
      }
    },
    [subsetId, selectFact, deselectFact],
  );

  const toggleAllHandler = async () => {
    if (allRowsChecked) {
      await handleToggleAll({ checked: false });
    } else {
      await handleToggleAll({ checked: true });
    }
  };

  function deselectAllFactsFromDb() {
    if (!subsetId) return;
    deselectAllFacts(subsetId);
    setSelectedFactIds(new Set());
  }

  const onSearchCall = async (searchValue?: string, useNaturalLanguage?: boolean) => {
    trackCustomEvent(TrackingEventsV2.FactSearch, { matterId, isAllDocuments: subset?.isAllDocuments });

    const value = searchValue ?? searchQueryInput;

    if (useNaturalLanguage && value.trim()) {
      buildFiltersWithNL(value);
      return;
    }

    // Regular search
    // Reset page to 0
    const newPaginationState = { pageIndex: 0, pageSize: pagination.pageSize };
    setPagination(newPaginationState);

    // Close the doc viewer if it is open
    setFilters((prev) => ({ ...prev, searchQuery: value }));
    setSearchQueryInput(value);
  };

  const clearSearch = async () => {
    setSearchQueryInput('');
    setFilters((prev) => ({ ...prev, searchQuery: '' }));
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  const handleRemoveFilter = (filterKey: string, value?: any) => {
    if (filterKey === 'searchQuery') {
      setSearchQueryInput('');
    }
    if (filterKey === 'personIds') {
      searchParams.delete('personId', value);
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'issueIds') {
      searchParams.delete('issueIds', value);
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'keyFacts') {
      searchParams.delete('viewKeyFacts');
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'relevance' && value === Relevance.Relevant) {
      searchParams.delete('viewRelevantFacts');
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'threadId') {
      searchParams.delete('threadId');
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'factIds') {
      searchParams.delete('factIds', value);
      setSearchParams(searchParams, { replace: true });
    }

    setFilters((prev) => removeFilterValue(prev, filterKey as keyof FactFilters, value));

    // Reset pagination when filters change
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const openDocViewer = (docId: string, factId: string) => {
    setIsDocViewerOpen(true);
    setDocId(docId);
    setFactId(factId);
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const handlePageSizeChange = (newPageSize: number) => {
    const newPaginationState = { pageIndex: 0, pageSize: newPageSize };
    setPagination(newPaginationState);
  };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
    setSearchQueryInput('');

    // Clear URL params for key facts and disputed facts
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('viewKeyFacts');
    newParams.delete('viewDisputedFacts');
    newParams.delete('viewRelevantFacts');
    newParams.delete('issueIds');
    newParams.delete('threadId');
    // Remove all personId and factIds params
    const allKeys = Array.from(newParams.keys());
    allKeys.forEach((key) => {
      if (key === 'personId' || key === 'factIds') {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });

    // Reset pagination to first page
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  // useEffects for query param filters
  useEffect(() => {
    const personIds = searchParams.getAll('personId');
    if (personIds.length === 0) {
      return;
    }
    setFilters((prevFilters) => ({ ...prevFilters, personIds }));
  }, [searchParams]);

  useEffect(() => {
    const factIds = searchParams.getAll('factIds');
    if (factIds.length === 0) {
      return;
    }
    setFilters((prevFilters) => ({ ...prevFilters, factIds }));
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams.has('threadId')) {
      return;
    }
    const threadId = searchParams.get('threadId');
    setFilters((prevFilters) => {
      if (prevFilters.threadId === (threadId ?? undefined)) {
        return prevFilters;
      }
      return { ...prevFilters, threadId: threadId ?? undefined };
    });
  }, [searchParams]);

  useEffect(() => {
    const issueIds = searchParams.getAll('issueIds');
    if (issueIds.length === 0) {
      return;
    }
    setFilters((prevFilters) => ({ ...prevFilters, issueIds }));
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('viewKeyFacts') !== 'true') {
      return;
    }
    setFilters((prevFilters) => {
      if (prevFilters.keyFacts) {
        return prevFilters;
      }
      return { ...prevFilters, keyFacts: true };
    });
  }, [searchParams, setFilters]);

  useEffect(() => {
    if (searchParams.get('viewRelevantFacts') !== 'true') {
      return;
    }
    setFilters((prevFilters) => {
      const hasRelevantOnly =
        Array.isArray(prevFilters.relevance) &&
        prevFilters.relevance.length === 1 &&
        prevFilters.relevance[0] === Relevance.Relevant;
      if (hasRelevantOnly) {
        return prevFilters;
      }
      return { ...prevFilters, relevance: [Relevance.Relevant] };
    });
  }, [searchParams, setFilters]);

  // Check if all facts on current page are selected
  const allRowsChecked = useMemo(() => {
    if (!responseFacts?.facts || responseFacts.facts.length === 0) {
      return false;
    }
    return responseFacts.facts.every((fact) => selectedFactIds.has(fact.id));
  }, [responseFacts?.facts, selectedFactIds]);

  useEffect(() => {
    trackPageView(PageName.Facts);
  }, []);

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <FactsTableHeader
            // Search
            globalFilter={searchQueryInput}
            clearSearch={clearSearch}
            setGlobalFilter={setSearchQueryInput}
            onSearchCall={onSearchCall}
            refetchCaseFacts={refetchCaseFacts}
            goToPage={goToPage}
            activeFilters={filters}
            setFilters={setFilters}
            caseRuns={runs?.filter((run) => run.type === RunType.Regular || run.type === RunType.AddDocs) ?? []}
            onClusterComplete={() => {
              refetchCaseFacts();
            }}
            processingNL={isProcessingNLFiltersQuery}
            filterCount={
              Object.entries(filters).filter(
                ([key, value]) =>
                  // Exclude keyFacts and disputedFacts when false
                  !((key === 'keyFacts' || key === 'disputedFacts') && value === false) &&
                  value !== undefined &&
                  value !== null &&
                  value !== '' &&
                  !(Array.isArray(value) && value.length === 0),
              ).length
            }
            selectedFactIds={selectedFactIds}
            setSelectedFactIds={setSelectedFactIds}
            totalUnfilteredCount={responseFacts?.totalUnfilteredCount}
          />

          <FilterPills
            pills={buildFactFilterPills(filters, handleRemoveFilter, personsMap, issuesMap)}
            onClearAll={handleClearAllFilters}
          />

          <div className="flex items-center justify-between pt-4 pb-2">
            <div className="flex items-center gap-3 pl-1 text-xs text-gray-500">
              <Checkbox
                checked={allRowsChecked}
                onCheckedChange={toggleAllHandler}
                disabled={isLoading || isAllIdsLoading}
              />
              <p>
                <strong>{selectedFactIds.size.toLocaleString()}</strong> Facts selected
              </p>

              <Button size="sm" disabled={selectedFactIds.size === 0} onClick={deselectAllFactsFromDb}>
                Clear All ({selectedFactIds.size}) Selected Facts
              </Button>
            </div>

            <Pagination
              canGetPrevPage={pagination.pageIndex > 0}
              canGetNextPage={pagination.pageIndex < (responseFacts?.pageCount ?? 0) - 1}
              prevPage={prevPage}
              nextPage={nextPage}
              currentPage={pagination.pageIndex}
              noOfPages={responseFacts?.pageCount}
              goToPage={goToPage}
              pageSize={pagination.pageSize}
              onPageSizeChange={handlePageSizeChange}
              totalCount={responseFacts?.totalUnfilteredCount}
              filteredCount={responseFacts?.totalCount}
              entityName="facts"
            />
          </div>
        </PageContainer>

        <div className="flex w-full flex-1 gap-1 overflow-hidden">
          {isLoading ? (
            <FactsTableLoader />
          ) : (
            <div
              className={`h-full overflow-y-scroll transition-all duration-300 ${isDocViewerOpen ? 'w-7/12' : 'w-full'}`}
            >
              <FactsTable
                selectedFactId={factId ?? ''}
                facts={responseFacts?.facts || []}
                openDocViewer={openDocViewer}
                selectedFactIds={selectedFactIds}
                onFactSelectionChange={handleFactSelectionChange}
              />
            </div>
          )}
          <div
            className={`transition-all duration-300 ease-in-out ${
              isDocViewerOpen ? 'mr-2 mb-2 w-5/12 opacity-100' : 'w-0 opacity-0'
            } overflow-hidden rounded-md border border-gray-300 bg-white`}
          >
            <div
              className={`h-full w-full transition-transform duration-300 ease-in-out ${
                isDocViewerOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              {isDocViewerOpen && docId && matterId && factId && (
                <FactDocViewer
                  docId={docId}
                  factId={factId}
                  onClose={() => {
                    setDocId(null);
                    setFactId(null);
                    setIsDocViewerOpen(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
